import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { BACKEND_URL, getToken, api, mediaUrl } from "../lib/api";
import { useAuth } from "./AuthContext";
import { useSiteContent } from "./SiteContentContext";
import { toast } from "sonner";

const RealtimeContext = createContext(null);

export function RealtimeProvider({ children }) {
  const { user, patchUser } = useAuth();
  const { content } = useSiteContent();
  const wsRef = useRef(null);
  const audioRef = useRef(null);
  const [online, setOnline] = useState(new Set());
  const [unread, setUnread] = useState(0);
  const msgHandlers = useRef(new Set());
  const typingHandlers = useRef(new Set());
  const convHandlers = useRef(new Set());
  const visitHandlers = useRef(new Set());
  const profileHandlers = useRef(new Set());
  const groupHandlers = useRef(new Set());
  const groupMsgHandlers = useRef(new Set());
  const readHandlers = useRef(new Set());
  const spotMsgHandlers = useRef(new Set());
  const reactionHandlers = useRef(new Set());

  const playNotifSound = useCallback(() => {
    try {
      if (user && user.sound_enabled === false) return;
      const src = (user && user.notification_sound_url) || content.notification_sound_url;
      if (!src) return;
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = mediaUrl(src);
      audioRef.current.currentTime = 0;
      audioRef.current.volume = 0.7;
      audioRef.current.play().catch(() => {});
    } catch (e) {}
  }, [user, content.notification_sound_url]);

  const refreshNotifs = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setUnread(data.unread);
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (!user) return;
    refreshNotifs();
    const token = getToken();
    const wsUrl = BACKEND_URL.replace(/^http/, "ws") + `/api/ws`;
    let ws;
    let closed = false;
    const connect = () => {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => { try { ws.send(JSON.stringify({ type: "auth", token })); } catch (e) {} };
      ws.onmessage = (ev) => {
        const msg = JSON.parse(ev.data);
        if (msg.type === "presence") {
          setOnline((prev) => {
            const next = new Set(prev);
            if (msg.online) next.add(msg.user_id); else next.delete(msg.user_id);
            return next;
          });
        } else if (msg.type === "notification") {
          setUnread((u) => u + 1);
          playNotifSound();
          const n = msg.data;
          toast(`${n.actor_name} ${n.message}`, { description: "Spotter", duration: 4000 });
        } else if (msg.type === "message") {
          playNotifSound();
          msgHandlers.current.forEach((h) => h(msg.data));
          convHandlers.current.forEach((h) => h());
        } else if (msg.type === "typing") {
          typingHandlers.current.forEach((h) => h(msg));
        } else if (msg.type === "visit") {
          visitHandlers.current.forEach((h) => h(msg.count));
        } else if (msg.type === "profile_update") {
          if (msg.user && user && msg.user.id === user.id) patchUser(msg.user);
          profileHandlers.current.forEach((h) => h(msg.user));
        } else if (msg.type === "admin_group_message") {
          playNotifSound();
          groupHandlers.current.forEach((h) => h(msg.data));
        } else if (msg.type === "group_message") {
          playNotifSound();
          groupMsgHandlers.current.forEach((h) => h(msg.data));
          convHandlers.current.forEach((h) => h());
        } else if (msg.type === "spot_message") {
          playNotifSound();
          spotMsgHandlers.current.forEach((h) => h(msg.data));
        } else if (msg.type === "read_receipt") {
          readHandlers.current.forEach((h) => h(msg));
        } else if (msg.type === "message_reaction") {
          reactionHandlers.current.forEach((h) => h(msg));
        } else if (msg.type === "conv_update") {
          convHandlers.current.forEach((h) => h());
        }
      };
      ws.onclose = () => { if (!closed) setTimeout(connect, 2000); };
    };
    connect();
    return () => { closed = true; if (ws) ws.close(); };
  }, [user, refreshNotifs]);

  const sendTyping = (toId, typing, channel = "dm", channelId = null) => {
    try { wsRef.current?.send(JSON.stringify({ type: "typing", to: toId, typing, channel, channel_id: channelId })); } catch (e) {}
  };

  const registerMessageHandler = (fn) => { msgHandlers.current.add(fn); return () => msgHandlers.current.delete(fn); };
  const registerTypingHandler = (fn) => { typingHandlers.current.add(fn); return () => typingHandlers.current.delete(fn); };
  const registerConvHandler = (fn) => { convHandlers.current.add(fn); return () => convHandlers.current.delete(fn); };
  const registerVisitHandler = (fn) => { visitHandlers.current.add(fn); return () => visitHandlers.current.delete(fn); };
  const registerProfileHandler = (fn) => { profileHandlers.current.add(fn); return () => profileHandlers.current.delete(fn); };
  const registerGroupHandler = (fn) => { groupHandlers.current.add(fn); return () => groupHandlers.current.delete(fn); };
  const registerGroupMsgHandler = (fn) => { groupMsgHandlers.current.add(fn); return () => groupMsgHandlers.current.delete(fn); };
  const registerReadHandler = (fn) => { readHandlers.current.add(fn); return () => readHandlers.current.delete(fn); };
  const registerSpotMsgHandler = (fn) => { spotMsgHandlers.current.add(fn); return () => spotMsgHandlers.current.delete(fn); };
  const registerReactionHandler = (fn) => { reactionHandlers.current.add(fn); return () => reactionHandlers.current.delete(fn); };

  const isOnline = (id) => online.has(id);

  return (
    <RealtimeContext.Provider value={{ isOnline, online, unread, setUnread, refreshNotifs, sendTyping, registerMessageHandler, registerTypingHandler, registerConvHandler, registerVisitHandler, registerProfileHandler, registerGroupHandler, registerGroupMsgHandler, registerReadHandler, registerSpotMsgHandler, registerReactionHandler }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export const useRealtime = () => useContext(RealtimeContext);
